import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import { PluginModal } from '../../components/PluginModal';
import { type ExistingVolumeOptionItem, ExistingVolumeSelectField } from '../../components/existingVolumeSelect';
import { getModalPopupContainer } from '../../utils/modalPopupContainer';
import styles from './index.module.less';
import type { ImageVmSelectModalProps } from './types';
import { buildSystemDiskFromExistingSelection } from './utils';

enum Field {
  SELECTED_DISK = 'selectedDisk',
  DISK = 'disk',
}

/** 选择已有系统云盘弹窗 */
export function ImageVmSelectModal({ visible, form, title, namespace, onOk, onCancel }: ImageVmSelectModalProps) {
  const { t } = useTranslation();
  const openInitializedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      openInitializedRef.current = false;
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;
    form.resetFields();
  }, [form, visible]);

  const handleVolumeSelect = useCallback(
    (selected: ExistingVolumeOptionItem | undefined) => {
      if (!selected) {
        form.setFieldValue(Field.DISK, undefined);
        return;
      }
      form.setFieldValue(Field.DISK, buildSystemDiskFromExistingSelection(selected));
    },
    [form],
  );

  const handleOk = useCallback(async () => {
    try {
      await form.validate();
    } catch {
      return Promise.reject();
    }
    await onOk();
  }, [form, onOk]);

  const modalTitle = title ?? t('components:add.existing.system.disk');

  return (
    <PluginModal className={styles.selectModal} visible={visible} title={modalTitle} focusLock={false} onCancel={onCancel} onOk={handleOk}>
      <div className={styles.modalBody}>
        <Form form={form} layout="vertical">
          <ExistingVolumeSelectField
            form={form}
            field={Field.SELECTED_DISK}
            volumeType="System"
            namespace={namespace}
            enabled={visible}
            getPopupContainer={getModalPopupContainer}
            onVolumeSelect={handleVolumeSelect}
          />
          <Form.Item field={Field.DISK} hidden>
            <Input />
          </Form.Item>
        </Form>
      </div>
    </PluginModal>
  );
}

export function getImageVmSelectModalValues(form: ImageVmSelectModalProps['form']) {
  return form.getFieldValue(Field.DISK) as ReturnType<typeof buildSystemDiskFromExistingSelection> | undefined;
}

export type { ExistingVolumeOptionItem as ExistingDiskOptionItem } from '../../components/existingVolumeSelect';
